import { supabase } from "~/integrations/supabase/client";
import type {
  IngestionPallet,
  IngestionPalletStatus,
  IngestionRequest,
  IngestionRequestStatus,
  IngestionWorkflowStep,
  PalletValidationResult,
} from "~/types/wms";

export interface IngestionRequestFilters {
  status?: IngestionRequestStatus | IngestionRequestStatus[];
  workflow_step?: IngestionWorkflowStep | IngestionWorkflowStep[];
  product_id?: string;
  assigned_to?: string;
}

export const ingestionService = {
  // ============================================================================
  // Ingestion Requests
  // ============================================================================

  async getIngestionRequests(
    filters?: IngestionRequestFilters
  ): Promise<IngestionRequest[]> {
    let query = supabase
      .from("wms_ingestion_requests")
      .select(
        `
        *,
        product:products(*),
        production_batch:production_batches(batch_number, expiry_date),
        assigned_profile:profiles!wms_ingestion_requests_assigned_to_fkey(name, lastname),
        placed_position:wms_positions(id, code, full_code, barcode)
      `
      )
      .order("created_at", { ascending: false });

    if (filters?.status) {
      if (Array.isArray(filters.status)) {
        query = query.in("status", filters.status);
      } else {
        query = query.eq("status", filters.status);
      }
    }

    if (filters?.workflow_step) {
      if (Array.isArray(filters.workflow_step)) {
        query = query.in("workflow_step", filters.workflow_step);
      } else {
        query = query.eq("workflow_step", filters.workflow_step);
      }
    }

    if (filters?.product_id) {
      query = query.eq("product_id", filters.product_id);
    }

    if (filters?.assigned_to) {
      query = query.eq("assigned_to", filters.assigned_to);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as IngestionRequest[];
  },

  async getIngestionRequestById(id: string): Promise<IngestionRequest> {
    const { data, error } = await supabase
      .from("wms_ingestion_requests")
      .select(
        `
        *,
        product:products(*),
        production_batch:production_batches(batch_number, expiry_date),
        assigned_profile:profiles!wms_ingestion_requests_assigned_to_fkey(name, lastname),
        placed_position:wms_positions(id, code, full_code, barcode)
      `
      )
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as IngestionRequest;
  },

  async claimIngestionRequest(
    id: string,
    userId: string
  ): Promise<IngestionRequest> {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from("wms_ingestion_requests")
      .update({
        assigned_to: userId,
        assigned_at: now,
        status: "assigned",
        workflow_step: "claimed",
        started_at: now,
      })
      .eq("id", id)
      .eq("status", "pending") // Only claim if status is pending
      .eq("workflow_step", "pending") // Only claim if workflow is pending
      .select(
        `
        *,
        production_batch:production_batches(*),
        product:products(*)
      `
      )
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        throw new Error("Esta tarea ya fue reclamada por otro usuario");
      }
      throw error;
    }
    return data as IngestionRequest;
  },

  async updateIngestionRequestStatus(
    id: string,
    status: IngestionRequestStatus,
    additionalData?: {
      received_pallets?: number;
      received_boxes?: number;
      completed_at?: string;
    }
  ): Promise<IngestionRequest> {
    const updateData: Record<string, unknown> = {
      status,
      updated_at: new Date().toISOString(),
      ...additionalData,
    };

    if (status === "completed" && !additionalData?.completed_at) {
      updateData.completed_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from("wms_ingestion_requests")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data as IngestionRequest;
  },

  // ============================================================================
  // Workflow Functions (3-step inbound)
  // ============================================================================

  /**
   * Verify batch barcode matches the claimed ingestion request
   * Updates workflow_step from 'claimed' to 'verified'
   */
  async verifyBatchBarcode(
    requestId: string,
    scannedBatchNumber: string
  ): Promise<{ valid: boolean; request?: IngestionRequest; error?: string }> {
    // Get the request with batch info
    const { data: request, error: fetchError } = await supabase
      .from("wms_ingestion_requests")
      .select(
        `
        *,
        product:products(*),
        production_batch:production_batches(id, batch_number, expiry_date)
      `
      )
      .eq("id", requestId)
      .single();

    if (fetchError || !request) {
      return { valid: false, error: "Solicitud de ingreso no encontrada" };
    }

    if (request.workflow_step !== "claimed") {
      return {
        valid: false,
        error: `Estado inválido: ${request.workflow_step}. Debe estar reclamado.`,
      };
    }

    // Check if scanned batch matches expected
    const expectedBatchNumber = request.production_batch?.batch_number;
    if (!expectedBatchNumber) {
      return {
        valid: false,
        error: "No se encontró el número de lote esperado",
      };
    }

    // Normalize comparison (trim, case-insensitive)
    const normalizedScanned = scannedBatchNumber.trim().toUpperCase();
    const normalizedExpected = expectedBatchNumber.trim().toUpperCase();

    if (normalizedScanned !== normalizedExpected) {
      return {
        valid: false,
        error: `Lote incorrecto. Esperado: ${expectedBatchNumber}, Escaneado: ${scannedBatchNumber}`,
      };
    }

    // Update to verified status
    const { data: updatedRequest, error: updateError } = await supabase
      .from("wms_ingestion_requests")
      .update({
        workflow_step: "verified",
        verified_at: new Date().toISOString(),
        status: "scanning",
      })
      .eq("id", requestId)
      .select(
        `
        *,
        product:products(*),
        production_batch:production_batches(id, batch_number, expiry_date)
      `
      )
      .single();

    if (updateError) {
      return { valid: false, error: "Error al actualizar el estado" };
    }

    // Increment product stock when batch is verified (received)
    const { error: stockError } = await supabase.rpc(
      "increment_product_stock",
      {
        p_product_id: request.product_id,
        p_quantity: request.expected_boxes,
      }
    );

    if (stockError) {
      console.error("Error incrementing product stock:", stockError);
      // Don't throw - the verification succeeded, stock update is secondary
    }

    return { valid: true, request: updatedRequest as IngestionRequest };
  },

  /**
   * Place batch in position - final step of ingestion workflow
   * Creates inventory record and updates workflow_step to 'completed'
   */
  async placeBatchInPosition(
    requestId: string,
    positionId: string,
    boxCount: number,
    userId: string
  ): Promise<{ success: boolean; request?: IngestionRequest; error?: string }> {
    // Get the request
    const { data: request, error: fetchError } = await supabase
      .from("wms_ingestion_requests")
      .select(
        `
        *,
        product:products(id, short_name),
        production_batch:production_batches(id, batch_number, expiry_date)
      `
      )
      .eq("id", requestId)
      .single();

    if (fetchError || !request) {
      return { success: false, error: "Solicitud de ingreso no encontrada" };
    }

    if (request.workflow_step !== "verified") {
      return {
        success: false,
        error: `Estado inválido: ${request.workflow_step}. Debe estar verificado.`,
      };
    }

    // Validate position exists
    const { data: position, error: posError } = await supabase
      .from("wms_positions")
      .select("id, full_code, is_active")
      .eq("id", positionId)
      .single();

    if (posError || !position) {
      return { success: false, error: "Posición no encontrada" };
    }

    if (!position.is_active) {
      return { success: false, error: "La posición no está activa" };
    }

    const now = new Date().toISOString();

    // Create inventory record
    const { error: invError } = await supabase.from("wms_inventory").insert({
      position_id: positionId,
      product_id: request.product_id,
      production_batch_id: request.production_batch_id,
      lot_number: request.production_batch?.batch_number,
      box_count: boxCount,
      reserved_boxes: 0,
      expiration_date: request.production_batch?.expiry_date,
      received_at: now,
    });

    if (invError) {
      return {
        success: false,
        error: `Error al crear inventario: ${invError.message}`,
      };
    }

    // Record movement
    await supabase.from("wms_movements").insert({
      movement_type: "receipt",
      product_id: request.product_id,
      lot_number: request.production_batch?.batch_number,
      box_quantity: boxCount,
      to_position_id: positionId,
      reference_type: "ingestion_request",
      reference_id: requestId,
      performed_by: userId,
      performed_at: now,
    });

    // Update ingestion request to completed
    const { data: updatedRequest, error: updateError } = await supabase
      .from("wms_ingestion_requests")
      .update({
        workflow_step: "completed",
        status: "completed",
        placed_position_id: positionId,
        placed_quantity: boxCount,
        received_boxes: boxCount,
        completed_at: now,
      })
      .eq("id", requestId)
      .select(
        `
        *,
        product:products(*),
        production_batch:production_batches(id, batch_number, expiry_date),
        placed_position:wms_positions(id, code, full_code, barcode)
      `
      )
      .single();

    if (updateError) {
      return { success: false, error: "Error al completar la solicitud" };
    }

    return { success: true, request: updatedRequest as IngestionRequest };
  },

  /**
   * Cancel/abandon an ingestion request
   * Resets workflow_step to 'pending' if not completed
   */
  async abandonIngestionRequest(requestId: string): Promise<IngestionRequest> {
    const { data, error } = await supabase
      .from("wms_ingestion_requests")
      .update({
        workflow_step: "pending",
        status: "pending",
        assigned_to: null,
        assigned_at: null,
        started_at: null,
        verified_at: null,
      })
      .eq("id", requestId)
      .neq("workflow_step", "completed") // Can't abandon completed requests
      .select()
      .single();

    if (error) throw error;
    return data as IngestionRequest;
  },

  /**
   * Find and claim ingestion request by batch number (scan-first workflow)
   * Looks up pending request by batch number and auto-claims it
   */
  async findAndClaimByBatchNumber(
    batchNumber: string,
    userId: string
  ): Promise<{ success: boolean; request?: IngestionRequest; error?: string }> {
    // Normalize the batch number (handle barcode format BATCH:xxx)
    const normalizedBatchNumber = batchNumber
      .replace(/^BATCH:/i, "")
      .trim()
      .toUpperCase();

    // 1. Find pending ingestion request by batch number
    const { data: requests, error: findError } = await supabase
      .from("wms_ingestion_requests")
      .select(
        `
        *,
        production_batch:production_batches!inner(id, batch_number, expiry_date),
        product:products(*)
      `
      )
      .eq("status", "pending")
      .eq("workflow_step", "pending");

    if (findError) {
      return { success: false, error: "Error al buscar solicitud de ingreso" };
    }

    // Filter by batch number (case-insensitive)
    const matchingRequest = requests?.find(
      (r) =>
        r.production_batch?.batch_number?.toUpperCase() ===
        normalizedBatchNumber
    );

    if (!matchingRequest) {
      return {
        success: false,
        error: `No se encontró solicitud pendiente para el lote: ${normalizedBatchNumber}`,
      };
    }

    // 2. Claim the request and mark as verified (skip separate claim step)
    const now = new Date().toISOString();
    const { data: claimed, error: claimError } = await supabase
      .from("wms_ingestion_requests")
      .update({
        assigned_to: userId,
        assigned_at: now,
        status: "assigned",
        workflow_step: "verified",
        started_at: now,
        verified_at: now,
      })
      .eq("id", matchingRequest.id)
      .eq("status", "pending") // Ensure still pending (race condition protection)
      .select(
        `
        *,
        production_batch:production_batches(id, batch_number, expiry_date),
        product:products(*)
      `
      )
      .single();

    if (claimError) {
      if (claimError.code === "PGRST116") {
        return {
          success: false,
          error: "Este lote ya fue reclamado por otro usuario",
        };
      }
      return { success: false, error: "Error al reclamar la solicitud" };
    }

    // Increment product stock when batch is verified (received)
    const { error: stockError } = await supabase.rpc(
      "increment_product_stock",
      {
        p_product_id: claimed.product_id,
        p_quantity: claimed.expected_boxes,
      }
    );

    if (stockError) {
      console.error("Error incrementing product stock:", stockError);
      // Don't throw - the claim succeeded, stock update is secondary
    }

    return { success: true, request: claimed as IngestionRequest };
  },

  /**
   * Get batches that have been received but not yet placed in a position
   * These are requests with workflow_step = 'verified' but not completed
   */
  async getReceivedAwaitingPlacement(): Promise<IngestionRequest[]> {
    const { data, error } = await supabase
      .from("wms_ingestion_requests")
      .select(
        `
        *,
        product:products(id, short_name),
        production_batch:production_batches(id, batch_number, expiry_date),
        assigned_profile:profiles!wms_ingestion_requests_assigned_to_fkey(name, lastname)
      `
      )
      .eq("workflow_step", "verified")
      .is("completed_at", null)
      .order("verified_at", { ascending: true }); // FIFO - oldest verified first

    if (error) throw error;
    return (data || []) as IngestionRequest[];
  },

  /**
   * Get pending tasks for worker task queue with pagination
   * Returns pending requests plus requests claimed by this user
   */
  async getWorkerTasks(
    userId: string,
    options?: { page?: number; pageSize?: number }
  ): Promise<{
    pending: IngestionRequest[];
    myTasks: IngestionRequest[];
    totalPending: number;
    page: number;
    pageSize: number;
  }> {
    const page = options?.page ?? 1;
    const pageSize = options?.pageSize ?? 10;
    const offset = (page - 1) * pageSize;

    // Get total count of pending tasks
    const { count: totalPending, error: countError } = await supabase
      .from("wms_ingestion_requests")
      .select("*", { count: "exact", head: true })
      .eq("workflow_step", "pending")
      .eq("status", "pending");

    if (countError) throw countError;

    // Get pending tasks with pagination
    const { data: pending, error: pendingError } = await supabase
      .from("wms_ingestion_requests")
      .select(
        `
        *,
        product:products(id, short_name),
        production_batch:production_batches(batch_number, expiry_date)
      `
      )
      .eq("workflow_step", "pending")
      .eq("status", "pending")
      .order("priority", { ascending: true })
      .order("created_at", { ascending: true })
      .range(offset, offset + pageSize - 1);

    if (pendingError) throw pendingError;

    // Get my active tasks (claimed or verified) - only if userId is provided
    let myTasks: IngestionRequest[] = [];
    if (userId) {
      const { data, error: myError } = await supabase
        .from("wms_ingestion_requests")
        .select(
          `
          *,
          product:products(id, short_name),
          production_batch:production_batches(batch_number, expiry_date),
          placed_position:wms_positions(id, code, full_code)
        `
        )
        .eq("assigned_to", userId)
        .in("workflow_step", ["claimed", "verified"])
        .order("assigned_at", { ascending: false });

      if (myError) throw myError;
      myTasks = (data || []) as IngestionRequest[];
    }

    return {
      pending: (pending || []) as IngestionRequest[],
      myTasks,
      totalPending: totalPending ?? 0,
      page,
      pageSize,
    };
  },

  // ============================================================================
  // Ingestion Pallets
  // ============================================================================

  async getIngestionPallets(
    ingestionRequestId: string
  ): Promise<IngestionPallet[]> {
    const { data, error } = await supabase
      .from("wms_ingestion_pallets")
      .select(
        `
        *,
        pallet:wms_pallets(*),
        destination_position:wms_positions(full_code, barcode)
      `
      )
      .eq("ingestion_request_id", ingestionRequestId)
      .order("scanned_at", { ascending: true });

    if (error) throw error;
    return data as IngestionPallet[];
  },

  async scanPallet(data: {
    ingestion_request_id: string;
    pallet_id: string;
    scanned_barcode: string;
    expected_boxes: number;
    received_boxes: number;
    destination_position_id: string;
    status: IngestionPalletStatus;
    discrepancy_type?: string;
    discrepancy_notes?: string;
    scanned_by: string;
  }): Promise<IngestionPallet> {
    const { data: palletScan, error } = await supabase
      .from("wms_ingestion_pallets")
      .insert({
        ...data,
        scanned_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;

    // Update pallet status and position
    await supabase
      .from("wms_pallets")
      .update({
        status: "received",
        position_id: data.destination_position_id,
        received_at: new Date().toISOString(),
        received_by: data.scanned_by,
        current_box_count: data.received_boxes,
      })
      .eq("id", data.pallet_id);

    // Create inventory record
    const { data: pallet } = await supabase
      .from("wms_pallets")
      .select("product_id, production_batch_id, lot_number, expiration_date")
      .eq("id", data.pallet_id)
      .single();

    if (pallet) {
      await supabase.from("wms_inventory").insert({
        position_id: data.destination_position_id,
        pallet_id: data.pallet_id,
        product_id: pallet.product_id,
        production_batch_id: pallet.production_batch_id,
        lot_number: pallet.lot_number,
        box_count: data.received_boxes,
        reserved_boxes: 0,
        expiration_date: pallet.expiration_date,
        received_at: new Date().toISOString(),
      });

      // Record movement
      await supabase.from("wms_movements").insert({
        movement_type: "receipt",
        pallet_id: data.pallet_id,
        product_id: pallet.product_id,
        lot_number: pallet.lot_number,
        box_quantity: data.received_boxes,
        to_position_id: data.destination_position_id,
        reference_type: "ingestion_request",
        reference_id: data.ingestion_request_id,
        scanned_pallet_barcode: data.scanned_barcode,
        performed_by: data.scanned_by,
        performed_at: new Date().toISOString(),
      });
    }

    return palletScan as IngestionPallet;
  },

  // ============================================================================
  // Statistics
  // ============================================================================

  async getIngestionStatistics(): Promise<{
    pending: number;
    in_progress: number;
    completed_today: number;
    total_pending_boxes: number;
  }> {
    const today = new Date().toISOString().split("T")[0];

    const [pending, inProgress, completedToday, pendingBoxes] =
      await Promise.all([
        supabase
          .from("wms_ingestion_requests")
          .select("id", { count: "exact", head: true })
          .eq("status", "pending"),
        supabase
          .from("wms_ingestion_requests")
          .select("id", { count: "exact", head: true })
          .in("status", ["assigned", "scanning", "putaway"]),
        supabase
          .from("wms_ingestion_requests")
          .select("id", { count: "exact", head: true })
          .eq("status", "completed")
          .gte("completed_at", `${today}T00:00:00`),
        supabase
          .from("wms_ingestion_requests")
          .select("expected_boxes")
          .in("status", ["pending", "assigned", "scanning", "putaway"]),
      ]);

    const totalPendingBoxes = (pendingBoxes.data || []).reduce(
      (sum, req) => sum + (req.expected_boxes || 0),
      0
    );

    return {
      pending: pending.count || 0,
      in_progress: inProgress.count || 0,
      completed_today: completedToday.count || 0,
      total_pending_boxes: totalPendingBoxes,
    };
  },

  /**
   * Lookup position by barcode or code
   * Handles both LOC:xxx barcode format and direct full_code input
   */
  async lookupPositionByBarcode(input: string): Promise<{
    position?: {
      id: string;
      code: string;
      full_code: string;
      barcode?: string;
    };
    error?: string;
  }> {
    // Normalize input - remove LOC: prefix if present
    const code = input.trim().replace(/^LOC:/i, "").toUpperCase();

    if (!code) {
      return { error: "Codigo de posicion vacio" };
    }

    // Try to find by barcode first, then by full_code
    const { data, error } = await supabase
      .from("wms_positions")
      .select("id, code, full_code, barcode, is_active")
      .or(`barcode.ilike.%${code}%,full_code.ilike.%${code}%`)
      .eq("is_active", true)
      .limit(1)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        return { error: `Posicion no encontrada: ${code}` };
      }
      return { error: "Error al buscar posicion" };
    }

    return {
      position: {
        id: data.id,
        code: data.code,
        full_code: data.full_code,
        barcode: data.barcode,
      },
    };
  },

  // ============================================================================
  // Pallet-Based Ingestion (new workflow)
  // ============================================================================

  /**
   * Validate if a pallet is ready for ingestion into the warehouse.
   * Calls the validate_pallet_for_ingestion RPC function.
   */
  async validatePalletForIngestion(
    palletBarcode: string
  ): Promise<PalletValidationResult> {
    // Normalize barcode - remove PALLET: prefix if present
    const normalizedBarcode = palletBarcode
      .trim()
      .replace(/^PALLET:/i, "")
      .toUpperCase();

    const { data, error } = await supabase.rpc(
      "validate_pallet_for_ingestion",
      {
        p_barcode: normalizedBarcode,
      }
    );

    if (error) {
      throw new Error(`Error validating pallet: ${error.message}`);
    }

    // RPC RETURNS TABLE gives an array; take the first row and map DB column names
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) {
      return {
        is_valid: false,
        pallet_id: null,
        pallet_code: null,
        product_id: null,
        product_name: null,
        box_count: null,
        lot_number: null,
        batch_number: null,
        expiration_date: null,
        sequence_number: null,
        total_pallets_in_batch: null,
        production_ended_at: null,
        current_status: null,
        error_message: "Pallet no encontrado",
      };
    }

    return {
      is_valid: row.is_valid,
      pallet_id: row.pallet_id,
      pallet_code: row.pallet_code,
      product_id: row.product_id,
      product_name: row.product_name,
      box_count: row.box_count,
      lot_number: row.lot_number,
      batch_number: row.batch_number,
      expiration_date: row.expiration_date,
      sequence_number: row.sequence_number,
      total_pallets_in_batch: row.total_pallets,
      production_ended_at: row.production_ended_at,
      current_status: row.status,
      error_message: row.message,
    };
  },

  /**
   * Ingest a pallet into a warehouse position.
   * Calls the ingest_pallet RPC function.
   */
  async ingestPallet(
    palletId: string,
    positionId: string,
    receivedBy: string
  ): Promise<{
    success: boolean;
    pallet_id: string;
    position_code: string;
    message?: string;
  }> {
    const { data, error } = await supabase.rpc("ingest_pallet", {
      p_pallet_id: palletId,
      p_position_id: positionId,
    });

    if (error) {
      throw new Error(`Error ingesting pallet: ${error.message}`);
    }

    return data;
  },
};
